import { useContext, useState, useEffect, useCallback } from "react"
import { DateTime } from "luxon"

//SOAR Context
import { UserContext } from "../../../context/userContext"

//primeReact
import { Dialog } from "primereact/dialog"
import { ConfirmDialog } from "primereact/confirmdialog" // For <ConfirmDialog /> component
import { confirmDialog } from "primereact/confirmdialog" // For confirmDialog method

//Formik
import { Form, Formik, useField } from "formik"
import * as Yup from "yup"

//Formik helpers
import FormikDropDown from "../../../helpers/formikInputs/FormikDropDown"
import FormikNumberInput from "../../../helpers/formikInputs/FormikNumberInput"
import FormikCalendar from "../../../helpers/formikInputs/FormikCalendar"
import FormikCheckBox from "../../../helpers/formikInputs/FormikCheckboxInput"

//Helpers
import { LogError } from "../../../helpers/logger"

//Housing API and components
import {
  housingApiAssignBedOccupancyPost,
  housingApiGetHouseGet,
  housingApiRemoveBedOccupancyPost,
} from "../housingApi"
import OccupantFormBookings from "./OccupantFormBookings"
import styles from "../../../styles/housing/HousingAllocations.module.css"
import OccupantFormRentFrequency from "./OccupantFormRentFrequency"

export default function OccupantForm({
  showForm,
  onHideForm,
  onOccupantAdded,
  activeParticipants,
  allParticipants,
  houseList,
  roomList,
  bedList,
  selectedBed,
  editBedAllocation,
}) {
  const userCtx = useContext(UserContext)
  const noRentDueValue = "No rent due"

  const newOccupant = useCallback(() => {
    let date = new Date()
    // add a day
    date.setDate(date.getDate() + 1)
    return {
      id: 0,
      bedId: 0,
      organizationId: userCtx.session.user.organization_id,
      participantId: undefined,
      start: new Date(),
      end: date,
      rentFrequency: "",
      rentFrequencyDetail: "",
      rentValue: "",
      overlapsOther: false,
      overlapsSelf: false,
      resolveOverlap: false,
      userId: userCtx.session.user.staff_id,
    }
  }, [userCtx.session.user.organization_id, userCtx.session.user.staff_id])

  const weeklyOptions = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ]
  const monthlyOptions = Array.from({ length: 31 }, (_, i) =>
    (i + 1).toString(),
  )

  const [occupant, setOccupant] = useState(newOccupant())
  const [frequencyOptions, setFrequencyOptions] = useState([])
  const [minEndDate, setMinEndDate] = useState(occupant.start)
  const [selectedHouseId, setSelectedHouseId] = useState(undefined)
  const [isLoadingHouse, setIsLoadingHouse] = useState(false)
  const [isDeletingHouse, setIsDeletingHouse] = useState(false)
  const [houseRoomList, setHouseRoomList] = useState([])
  const [houseBedList, setHouseBedList] = useState([])
  const [roomBedList, setRoomBedList] = useState([])
  const [availableParticipants, setAvailableParticipants] = useState([])
  const [availableHouses, setAvailableHouses] = useState(houseList)

  const getHouse = useCallback(async () => {
    try {
      if (!!selectedHouseId) {
        setIsLoadingHouse(true)
        let result = await housingApiGetHouseGet(
          userCtx.tokens.getHousingToken(),
          selectedHouseId,
        )

        if (!!result) {
          result.house.organizationIds = result.organizations.map(
            (organization) => organization.organizationId,
          )

          result.beds.forEach((bed) => {
            bed.isAllocated = result.bedOccupations.some(
              (occupation) => occupation.bedId === bed.id,
            )
          })

          setHouseRoomList(result.rooms)
          setHouseBedList(result.beds)

          setIsLoadingHouse(false)

          return {
            room: !!result.rooms ? result.rooms[0] : undefined,
            bed: !!result.beds ? result.beds[0] : undefined,
          }
        } else {
          throw new Error("House not found")
        }
      }
    } catch (error) {
      LogError("Unable to retrieve House profile", error)
      userCtx.setNotificationError("Unable to retrieve house profile")
      setIsLoadingHouse(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedHouseId, userCtx.setNotificationError])

  useEffect(() => {
    getHouse()
  }, [getHouse])

  useEffect(() => {
    if (!!selectedBed && occupant.bedId !== selectedBed.id) {
      let newProfile = newOccupant()
      newProfile.rentValue = selectedBed.rentValue
      newProfile.rentFrequency = selectedBed.rentFrequency
      newProfile.rentFrequencyDetail = selectedBed.rentFrequencyDetail
      newProfile.rentGraceDays = selectedBed.rentGraceDays || 0
      newProfile.houseId = selectedBed.room?.houseId
      newProfile.roomId = selectedBed.roomId
      newProfile.bedId = selectedBed.id

      if (!!editBedAllocation) {
        newProfile.id = editBedAllocation.id
        newProfile.organizationId = editBedAllocation.organizationId
        newProfile.participantId = editBedAllocation.participantId
        newProfile.rentValue = editBedAllocation.rentValue
        newProfile.rentFrequency = editBedAllocation.rentFrequency
        newProfile.rentFrequencyDetail = editBedAllocation.rentFrequencyDetail
        newProfile.rentGraceDays = editBedAllocation.rentGraceDays || 0
        newProfile.start = DateTime.fromISO(editBedAllocation.start, {
          zone: "utc",
        })
          .toLocal()
          .toJSDate()
        newProfile.end = DateTime.fromISO(editBedAllocation.end, {
          zone: "utc",
        })
          .toLocal()
          .toJSDate()
      }

      if (newProfile.rentFrequency === "Weekly") {
        setFrequencyOptions(weeklyOptions)
      } else if (newProfile.rentFrequency === "Monthly") {
        setFrequencyOptions(monthlyOptions)
      }

      setMinEndDate(newProfile.start)

      setOccupant(newProfile)

      const newFilteredRooms = roomList?.filter(
        (room) => room.houseId === newProfile.houseId,
      )
      setHouseRoomList(newFilteredRooms)

      const newFilteredBeds = bedList?.filter(
        (bed) => bed.roomId === newProfile.roomId,
      )
      setAvailableHouses(houseList)
      setHouseBedList(bedList)
      setRoomBedList(newFilteredBeds)

      if (!!newProfile.houseId && !!houseList && !!activeParticipants) {
        const house = houseList?.filter(
          (house) => house.house.id === newProfile.houseId,
        )[0]
        const eligibleParticipants = activeParticipants?.filter((participant) =>
          participant.programs.some((program) =>
            house.programs.some(
              (houseProgram) =>
                houseProgram.programId === program.program_identity_id,
            ),
          ),
        )
        setAvailableParticipants(eligibleParticipants)
      }

      /*setAvailableParticipants(eligibleParticipants) */
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBed])

  const filterParticipantsByHouseProgram = (houseId) => {
    if (!!houseList && !!activeParticipants) {
      if (!!houseId) {
        const house = houseList?.filter(
          (house) => house.house.id === houseId,
        )[0]
        //Filter to only include participants that have at least one program that matches the selected house's programs
        const eligibleParticipants = activeParticipants?.filter((participant) =>
          participant.programs.some((program) =>
            house.programs.some(
              (houseProgram) =>
                houseProgram.programId === program.program_identity_id,
            ),
          ),
        )
        setAvailableParticipants(eligibleParticipants)

        return eligibleParticipants
      } else {
        setAvailableParticipants(activeParticipants)

        return activeParticipants
      }
    }
  }

  const filterHouseByParticipantPrograms = (participantId) => {
    if (!!houseList && !!activeParticipants) {
      if (!!participantId) {
        const participant = activeParticipants.find(
          (participant) => participant.participant_id === participantId,
        )

        const eligibleHouses = houseList.filter((house) =>
          house.programs.some((houseProgram) =>
            participant.programs.some(
              (program) =>
                program.program_identity_id === houseProgram.programId,
            ),
          ),
        )

        setAvailableHouses(eligibleHouses)
      } else {
        setAvailableHouses(houseList)
      }
    }
  }

  const validationSchema = Yup.object().shape({
    participantId: Yup.string().required("Please select a participant"),
    start: Yup.date()
      .required("Please select a start date")
      .test(
        "noOverlap",
        "The selected period overlaps another participant's stay in this bed. Please change the period",
        function (value) {
          return !this.parent.overlapsOther
        },
      ),
    end: Yup.date()
      .min(Yup.ref("start"), "end date can't be before start date")
      .required("Please select an end date")
      .test(
        "noOverlap2",
        "The selected period overlaps another participant's stay in this bed. Please change the period",
        function (value) {
          return !this.parent.overlapsOther
        },
      ),
    rentFrequency: Yup.string().required("Please select rent frequency"),
    rentFrequencyDetail: Yup.string()
      .when("rentFrequency", {
        is: "Weekly",
        then: Yup.string()
          .required("Please select the day of week that the rent will be due")
          .nullable(),
      })
      .when("rentFrequency", {
        is: "Monthly",
        then: Yup.string()
          .required(
            "Please select the day of the month that the rent will be due",
          )
          .nullable(),
      })
      .nullable(),
    rentValue: Yup.number().required("Please enter rent value"),
    resolveOverlap: Yup.bool().test(
      "allowResolveOverlap",
      "The participant has overlapping bed allocations, please confirm that the pre-existing periods may be cleaned",
      function (value) {
        return !(this.parent.overlapsSelf && !value)
      },
    ),
  })

  const submitValues = async (values) => {
    try {
      if (!values.organizationId) {
        throw new Error("No organization id specified")
      }

      let submitValues = { ...values }
      submitValues.participantAlternateId =
        activeParticipants.find(
          (x) => x.participant_id === submitValues.participantId,
        )?.alias || ""

      submitValues.start = DateTime.fromJSDate(submitValues.start)
        .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
        .toUTC()
        .toISO()
      submitValues.end = DateTime.fromJSDate(submitValues.end)
        .set({ hour: 23, minute: 59, second: 59, millisecond: 0 })
        .toUTC()
        .toISO()

      //If this is an edit, make sure that the resolveOverlap is true.
      //This will only be false if the only overlap is the one being edited.
      if (!!editBedAllocation) {
        submitValues.resolveOverlap = true
      }

      let result = await housingApiAssignBedOccupancyPost(
        userCtx.tokens.getHousingToken(),
        submitValues,
      )
      if (!!result) {
        userCtx.setNotificationInformation("Bed has been successfully occupied")
        !!onOccupantAdded ? onOccupantAdded() : onHideForm()
      } else {
        userCtx.setNotificationError(
          "Unable to occupy bed. See console for details.",
        )
        LogError("Unable to occupy bed. ", result)
      }
      return true
    } catch (error) {
      if (error.response?.data.includes("Dates conflict")) {
        userCtx.setNotificationError(
          "The selected dates conflict with another occupation on this bed",
        )
      } else {
        LogError("Unable to occupy bed. ", error)
        userCtx.setNotificationError(
          "Unable to occupy bed. See console for details.",
        )
      }
      return false
    }
  }

  const removeFromBed = async () => {
    try {
      setIsDeletingHouse(true)

      let result = await housingApiRemoveBedOccupancyPost(
        userCtx.tokens.getHousingToken(),
        {
          id: editBedAllocation.id,
          userId: userCtx.session.user.staff_id,
        },
      )

      if (!!result && !!result.removalDate) {
        userCtx.setNotificationInformation(
          "The participant assignment has been removed",
        )
        !!onOccupantAdded ? onOccupantAdded() : onHideForm()
      } else {
        userCtx.setNotificationError("Unable to delete bed occupation.")
      }

      setIsDeletingHouse(false)
    } catch (error) {
      LogError("Unable to delete bed. ", error)
      userCtx.setNotificationError(
        "Unable to delete bed occupation. See console for details.",
      )
      setIsDeletingHouse(false)
    }
  }

  return (
    <Dialog
      header={"Assign Occupancy For " + selectedBed?.name}
      visible={showForm}
      onHide={() => {
        onHideForm()
      }}
      style={{ width: "800px", height: "600px" }}
    >
      <ConfirmDialog />
      <Formik
        initialValues={occupant}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={async (values) => {
          await submitValues(values)
        }}
      >
        {({ isSubmitting, values, setFieldValue }) => (
          <div className={styles.mainContainer}>
            <div className={styles.formContainer}>
              <Form>
                <FormikDropDown
                  name="participantId"
                  label="Occupying Participant"
                  helpText="Specify which participant will occupy this bed"
                  options={availableParticipants}
                  optionLabel="name2"
                  optionValue={"participant_id"}
                  showClear={true}
                  disabled={
                    isSubmitting || isDeletingHouse || !!editBedAllocation
                  }
                  onChangeCallback={(value) => {
                    filterHouseByParticipantPrograms(value)
                  }}
                />
                <FormikDropDown
                  name="houseId"
                  label="House"
                  helpText="The house to which the participant will be assigned"
                  options={availableHouses}
                  optionLabel="house.name"
                  optionValue={"house.id"}
                  showClear={true}
                  disabled={isSubmitting || isDeletingHouse}
                  onChangeCallback={async (value) => {
                    setSelectedHouseId(value)
                    setFieldValue("roomId", undefined)
                    setFieldValue("bedId", undefined)
                    const participantList =
                      filterParticipantsByHouseProgram(value)

                    //Check if the currently selected participant is still valid
                    const participant = activeParticipants.find(
                      (participant) =>
                        participant.participant_id === values.participantId,
                    )
                    if (
                      !!participant &&
                      !participantList.some(
                        (eligible) =>
                          eligible.participant_id ===
                          participant.participant_id,
                      )
                    ) {
                      setFieldValue("participantId", "")
                    }
                  }}
                />
                <FormikDropDown
                  name="roomId"
                  label="Room"
                  helpText="The room to which the participant will be assigned"
                  options={houseRoomList}
                  optionLabel="name"
                  optionValue={"id"}
                  disabled={isSubmitting || isLoadingHouse || isDeletingHouse}
                  onChangeCallback={(value) => {
                    const beds = houseBedList.filter(
                      (bed) => bed.roomId === value,
                    )
                    setRoomBedList(beds)

                    if (beds.length === 1) {
                      setFieldValue("bedId", beds[0].id)
                    }
                  }}
                />
                <FormikDropDown
                  name="bedId"
                  label="Bed"
                  helpText="The bed to which the participant will be assigned"
                  options={roomBedList}
                  optionLabel="name"
                  optionValue={"id"}
                  disabled={isSubmitting || isLoadingHouse || isDeletingHouse}
                />
                <FormikCalendar
                  name="start"
                  label="Start Date"
                  helpText="Specify move in date (dates prior to today may incur backdated rent)"
                  disabled={isSubmitting || isDeletingHouse}
                  onChangeCallBack={(value) => {
                    if (values.end < value) {
                      setFieldValue("end", value)
                    }
                    setMinEndDate(value)
                  }}
                />
                <FormikCalendar
                  name="end"
                  label="End Date"
                  helpText="Specify move out date"
                  disabled={isSubmitting || isDeletingHouse}
                  minDate={minEndDate}
                />
                <OccupantFormRentFrequency
                  isSubmitting={isSubmitting}
                  monthlyOptions={monthlyOptions}
                  weeklyOptions={weeklyOptions}
                  noRentDueValue={noRentDueValue}
                  onFrequencyOptionChange={(options) => {
                    setFrequencyOptions(options)
                  }}
                />
                {values.rentFrequency === "Weekly" ||
                values.rentFrequency === "Monthly" ? (
                  <FormikDropDown
                    name="rentFrequencyDetail"
                    label="Rent due day"
                    helpText="When will the rent be due for this bed?"
                    options={frequencyOptions}
                    disabled={isSubmitting || isDeletingHouse}
                  />
                ) : null}
                {values.rentFrequency !== noRentDueValue ? (
                  <>
                    <FormikNumberInput
                      name="rentValue"
                      label="Rent value"
                      helpText={"The rent value due for this occupancy"}
                      disabled={isSubmitting || isDeletingHouse}
                      minValue={0}
                      currencyCode={"USD"}
                    />
                    <FormikNumberInput
                      name="rentGraceDays"
                      label="Rent grace days"
                      helpText={
                        "The amount of days past the due date when rent becomes overdue"
                      }
                      disabled={isSubmitting || isDeletingHouse}
                      minValue={0}
                      minFractionDigits={0}
                    />
                  </>
                ) : null}

                {values.overlapsSelf && !values.overlapsOther ? (
                  <FormikCheckBox
                    name="resolveOverlap"
                    label="Resolve overlapping bed allocations"
                    helpText={
                      "Replace the selected participants overlapping occupations with this one in the relevant period"
                    }
                    disabled={isSubmitting || isDeletingHouse}
                  />
                ) : null}
                <div style={{ float: "right" }}>
                  <button
                    type="submit"
                    className="btn"
                    disabled={isSubmitting || isDeletingHouse}
                  >
                    Save
                  </button>
                  {!!values.id ? (
                    <button
                      type="button"
                      className="btn"
                      style={{ marginLeft: "10px" }}
                      disabled={isSubmitting || isDeletingHouse}
                      onClick={() => {
                        confirmDialog({
                          message:
                            "Are you sure you want to delete this bed assignment?",
                          header: "Confirmation",
                          icon: "pi pi-exclamation-triangle",
                          accept: removeFromBed,
                        })
                      }}
                    >
                      Delete
                    </button>
                  ) : null}

                  <button
                    type="button"
                    className="btn"
                    style={{ marginLeft: "10px" }}
                    onClick={() => {
                      onHideForm()
                    }}
                    disabled={isSubmitting || isDeletingHouse}
                  >
                    Cancel
                  </button>
                </div>
              </Form>
            </div>
            <div className={styles.occupantContainer}>
              <OccupantFormBookings
                bedId={values.bedId}
                startDate={values.start}
                endDate={values.end}
                participants={allParticipants}
                editId={values.id}
              />
            </div>
          </div>
        )}
      </Formik>
    </Dialog>
  )
}
